// src/components/templates/diary/DiaryRecordsView.jsx
import React, { useState, useMemo } from 'react';
import { 
  FileText, Plus, Search, Calendar, Tag, Trash2, Edit3, X, Check,
  ChevronDown, ChevronUp, MoreVertical, Copy, Pin, PinOff, Clock,
  TrendingUp, Filter, Grid3X3, List, Upload, Download, ExternalLink,
  Briefcase, User, Activity, DollarSign, Users, Lightbulb, Car, 
  Wrench, GraduationCap, Home, Package, Award, FileCheck, MapPin,
  ArrowUpCircle, ArrowDownCircle, Hash, CalendarClock
} from 'lucide-react';
import { Button, Card, Badge } from './UIComponents';

// Record types with specific fields
const RecordType = {
  TRIP: 'TRIP',
  MAINTENANCE: 'MAINTENANCE',
  TRAINING: 'TRAINING',
  TRANSACT: 'TRANSACT',
  PROJECT: 'PROJECT',
  ASSET: 'ASSET',
  GENERAL: 'GENERAL'
};

// Transaction types
const TransactionType = {
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE'
};

// General record measurement types
const MeasurementType = {
  QUANTITY: 'QUANTITY',
  DATETIME: 'DATETIME',
  DATERANGE: 'DATERANGE',
  AMOUNT: 'AMOUNT'
};

// Currency options
const CURRENCIES = [
  { code: 'USD', symbol: '$', name: 'US Dollar' },
  { code: 'EUR', symbol: '€', name: 'Euro' },
  { code: 'GBP', symbol: '£', name: 'British Pound' },
  { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
  { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
  { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
  { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
  { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
  { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' }
];

// --- RECORD TYPE CONFIG ---
const RECORD_CONFIGS = {
  [RecordType.TRIP]: {
    icon: Car,
    color: 'text-blue-600 bg-blue-50 border-blue-200',
    gradient: 'from-blue-400 to-cyan-500',
    fields: ['startOdometer', 'endOdometer', 'startLocation', 'endLocation', 'purpose', 'distance'],
    label: 'Trip'
  },
  [RecordType.MAINTENANCE]: {
    icon: Wrench,
    color: 'text-orange-600 bg-orange-50 border-orange-200',
    gradient: 'from-orange-400 to-red-500',
    fields: ['asset', 'cost', 'provider', 'nextDue', 'warranty'],
    label: 'Maintenance'
  },
  [RecordType.TRAINING]: {
    icon: GraduationCap,
    color: 'text-purple-600 bg-purple-50 border-purple-200',
    gradient: 'from-purple-400 to-indigo-500',
    fields: ['course', 'provider', 'certification', 'expiryDate', 'cost'],
    label: 'Training'
  },
  [RecordType.TRANSACT]: {
    icon: DollarSign,
    color: 'text-green-600 bg-green-50 border-green-200',
    gradient: 'from-green-400 to-emerald-500',
    fields: ['transactionType', 'amount', 'currency', 'category', 'merchant', 'paymentMethod', 'receipt'],
    label: 'Transaction'
  },
  [RecordType.PROJECT]: {
    icon: Briefcase,
    color: 'text-indigo-600 bg-indigo-50 border-indigo-200',
    gradient: 'from-indigo-400 to-blue-500',
    fields: ['client', 'milestone', 'budget', 'deadline', 'status'],
    label: 'Project'
  },
  [RecordType.ASSET]: {
    icon: Package,
    color: 'text-teal-600 bg-teal-50 border-teal-200',
    gradient: 'from-teal-400 to-cyan-500',
    fields: ['name', 'serialNumber', 'purchaseDate', 'value', 'location'],
    label: 'Asset'
  },
  [RecordType.GENERAL]: {
    icon: FileText,
    color: 'text-gray-600 bg-gray-50 border-gray-200',
    gradient: 'from-gray-400 to-gray-500',
    fields: [],
    label: 'General'
  }
};

// --- TRANSACTION TYPE BADGE ---
const TransactionTypeBadge = ({ type }) => {
  if (type === TransactionType.INCOME) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
        <ArrowUpCircle className="w-3 h-3" />
        Income
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
      <ArrowDownCircle className="w-3 h-3" />
      Expense
    </span>
  );
};

// --- RECORD TYPE BADGE ---
const RecordTypeBadge = ({ type }) => {
  const config = RECORD_CONFIGS[type] || RECORD_CONFIGS[RecordType.GENERAL];
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-lg border font-medium text-xs uppercase tracking-wide ${config.color}`}>
      <Icon className="w-4 h-4" />
      {config.label}
    </span>
  );
};

// --- MEASUREMENT SELECTOR ---
const MeasurementSelector = ({ selected = [], onChange }) => {
  const measurements = [
    { key: MeasurementType.QUANTITY, icon: Hash, label: 'Quantity', color: 'text-blue-600 bg-blue-50 border-blue-200' },
    { key: MeasurementType.DATETIME, icon: Clock, label: 'Date & Time', color: 'text-purple-600 bg-purple-50 border-purple-200' },
    { key: MeasurementType.DATERANGE, icon: CalendarClock, label: 'Date Range', color: 'text-indigo-600 bg-indigo-50 border-indigo-200' },
    { key: MeasurementType.AMOUNT, icon: DollarSign, label: 'Amount', color: 'text-green-600 bg-green-50 border-green-200' }
  ];

  const toggleMeasurement = (key) => {
    if (selected.includes(key)) {
      onChange(selected.filter(m => m !== key));
    } else {
      onChange([...selected, key]);
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-3">Measurements</label>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {measurements.map(({ key, icon: Icon, label, color }) => (
          <button
            key={key}
            type="button"
            onClick={() => toggleMeasurement(key)}
            className={`p-3 rounded-lg border-2 transition-all flex flex-col items-center gap-2 ${
              selected.includes(key)
                ? `${color} border-current shadow-sm`
                : 'border-gray-200 text-gray-400 hover:border-gray-300'
            }`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-xs font-medium text-center">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

// --- MEASUREMENT FIELDS ---
const MeasurementFields = ({ measurements = [], values = {}, onChange }) => {
  const handleChange = (field, value) => {
    onChange({ ...values, [field]: value });
  };

  return (
    <div className="space-y-4">
      {measurements.includes(MeasurementType.QUANTITY) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Quantity</label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              value={values.quantity || ''}
              onChange={e => handleChange('quantity', e.target.value)}
              placeholder="Enter quantity"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <input
              type="text"
              value={values.quantityUnit || ''}
              onChange={e => handleChange('quantityUnit', e.target.value)}
              placeholder="Unit (kg, m, etc.)"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}

      {measurements.includes(MeasurementType.DATETIME) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date & Time</label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={values.dateTime?.split('T')[0] || ''}
              onChange={e => handleChange('dateTime', e.target.value + 'T' + (values.dateTime?.split('T')[1] || '00:00'))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <input
              type="time"
              value={values.dateTime?.split('T')[1] || ''}
              onChange={e => handleChange('dateTime', (values.dateTime?.split('T')[0] || new Date().toISOString().split('T')[0]) + 'T' + e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
      )}

      {measurements.includes(MeasurementType.DATERANGE) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Date Range</label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="date"
              value={values.dateRangeStart || ''}
              onChange={e => handleChange('dateRangeStart', e.target.value)}
              placeholder="Start date"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <input
              type="date"
              value={values.dateRangeEnd || ''}
              onChange={e => handleChange('dateRangeEnd', e.target.value)}
              placeholder="End date"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      )}

      {measurements.includes(MeasurementType.AMOUNT) && (
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
          <div className="grid grid-cols-2 gap-3">
            <input
              type="number"
              step="0.01"
              value={values.amount || ''}
              onChange={e => handleChange('amount', e.target.value)}
              placeholder="0.00"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
            />
            <select
              value={values.currency || 'USD'}
              onChange={e => handleChange('currency', e.target.value)}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
            >
              {CURRENCIES.map(curr => (
                <option key={curr.code} value={curr.code}>
                  {curr.symbol} {curr.code}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
    </div>
  );
};

// --- RECORD CARD ---
const RecordCard = ({ record, onEdit, onDelete, onPin, onDownload, viewMode, resources = [] }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  const config = RECORD_CONFIGS[record.type] || RECORD_CONFIGS[RecordType.GENERAL];
  const Icon = config.icon;

  const handleCopy = () => {
    const text = `${record.title}\n${record.description}\n${JSON.stringify(record.context, null, 2)}`;
    navigator.clipboard.writeText(text);
    setShowMenu(false);
  };

  const getContextValue = (key) => {
    return record.context?.[key] || '';
  };

  const hasAttachments = record.attachments && record.attachments.length > 0;

  // Format amount with currency
  const formatAmount = (amount, currency = 'USD') => {
    const curr = CURRENCIES.find(c => c.code === currency);
    return `${curr?.symbol || '$'}${parseFloat(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (viewMode === 'grid') {
    return (
      <div className="break-inside-avoid">
        <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-5 hover:shadow-lg transition-all relative group ${record.isPinned ? 'ring-2 ring-amber-400 ring-offset-2' : ''}`}>
          {record.isPinned && (
            <div className="absolute -top-2 -right-2">
              <Pin className="w-5 h-5 text-amber-500 fill-amber-500 drop-shadow" />
            </div>
          )}

          <div className="flex justify-between items-start mb-3">
            <RecordTypeBadge type={record.type} />
            <div className="flex items-center gap-2">
              {/* Transaction type badge */}
              {record.type === RecordType.TRANSACT && record.context?.transactionType && (
                <TransactionTypeBadge type={record.context.transactionType} />
              )}
              
              {hasAttachments && (
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full flex items-center gap-1">
                  <Upload className="w-3 h-3" />
                  {record.attachments.length}
                </span>
              )}
              <div className="relative">
                <button 
                  onClick={(e) => { e.stopPropagation(); setShowMenu(!showMenu); }}
                  className="p-1.5 rounded-lg opacity-0 group-hover:opacity-100 hover:bg-gray-100 transition-all"
                >
                  <MoreVertical className="w-4 h-4 text-gray-400" />
                </button>

                {showMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
                    <div className="absolute right-0 top-full mt-1 w-36 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-20">
                      <button onClick={() => { onEdit?.(record); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <Edit3 className="w-4 h-4 mr-2" /> Edit
                      </button>
                      <button onClick={() => { onPin?.(record); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        {record.isPinned ? <PinOff className="w-4 h-4 mr-2" /> : <Pin className="w-4 h-4 mr-2" />}
                        {record.isPinned ? 'Unpin' : 'Pin'}
                      </button>
                      <button onClick={handleCopy} className="w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                        <Copy className="w-4 h-4 mr-2" /> Copy
                      </button>
                      <button onClick={() => { onDelete?.(record.id); setShowMenu(false); }} className="w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                        <Trash2 className="w-4 h-4 mr-2" /> Delete
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs text-gray-400 mb-3">
            <Calendar className="w-3 h-3" />
            <span>{new Date(record.startDate).toLocaleDateString()}</span>
            {record.endDate && (
              <>
                <span>→</span>
                <span>{new Date(record.endDate).toLocaleDateString()}</span>
              </>
            )}
          </div>

          <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{record.title}</h3>
          
          {record.description && (
            <p className="text-sm text-gray-600 mb-3 line-clamp-2">{record.description}</p>
          )}

          {/* Special display for transactions */}
          {record.type === RecordType.TRANSACT && record.context?.amount && (
            <div className={`mb-3 p-3 rounded-lg ${
              record.context.transactionType === TransactionType.INCOME 
                ? 'bg-green-50 border border-green-200' 
                : 'bg-red-50 border border-red-200'
            }`}>
              <div className={`text-2xl font-bold ${
                record.context.transactionType === TransactionType.INCOME 
                  ? 'text-green-700' 
                  : 'text-red-700'
              }`}>
                {record.context.transactionType === TransactionType.INCOME ? '+' : '-'}
                {formatAmount(record.context.amount, record.context.currency)}
              </div>
              {record.context.category && (
                <div className="text-xs text-gray-600 mt-1">{record.context.category}</div>
              )}
            </div>
          )}

          {/* General record measurements */}
          {record.type === RecordType.GENERAL && record.measurements && (
            <div className="mb-3 space-y-2">
              {record.measurements.includes(MeasurementType.QUANTITY) && record.measurementValues?.quantity && (
                <div className="flex items-center gap-2 text-sm">
                  <Hash className="w-4 h-4 text-blue-600" />
                  <span className="font-medium text-gray-700">
                    {record.measurementValues.quantity} {record.measurementValues.quantityUnit || 'units'}
                  </span>
                </div>
              )}
              {record.measurements.includes(MeasurementType.DATETIME) && record.measurementValues?.dateTime && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-purple-600" />
                  <span className="text-gray-700">
                    {new Date(record.measurementValues.dateTime).toLocaleString()}
                  </span>
                </div>
              )}
              {record.measurements.includes(MeasurementType.DATERANGE) && record.measurementValues?.dateRangeStart && (
                <div className="flex items-center gap-2 text-sm">
                  <CalendarClock className="w-4 h-4 text-indigo-600" />
                  <span className="text-gray-700">
                    {new Date(record.measurementValues.dateRangeStart).toLocaleDateString()} - {new Date(record.measurementValues.dateRangeEnd).toLocaleDateString()}
                  </span>
                </div>
              )}
              {record.measurements.includes(MeasurementType.AMOUNT) && record.measurementValues?.amount && (
                <div className="flex items-center gap-2 text-sm">
                  <DollarSign className="w-4 h-4 text-green-600" />
                  <span className="font-medium text-gray-700">
                    {formatAmount(record.measurementValues.amount, record.measurementValues.currency)}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Context Fields */}
          {record.context && Object.keys(record.context).length > 0 && (
            <div className="space-y-1.5 mb-3">
              {Object.entries(record.context)
                .filter(([key]) => !['transactionType', 'amount', 'currency'].includes(key)) // Skip already displayed transaction fields
                .slice(0, isExpanded ? undefined : 3)
                .map(([key, value]) => (
                  value && (
                    <div key={key} className="flex items-start text-xs">
                      <span className="text-gray-500 font-medium w-28 flex-shrink-0 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}:
                      </span>
                      <span className="text-gray-700 flex-1">{value}</span>
                    </div>
                  )
                ))}
              {Object.keys(record.context).filter(([key]) => !['transactionType', 'amount', 'currency'].includes(key)).length > 3 && (
                <button 
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-indigo-600 hover:text-indigo-700 text-xs font-medium flex items-center"
                >
                  {isExpanded ? <ChevronUp className="w-3 h-3 mr-1" /> : <ChevronDown className="w-3 h-3 mr-1" />}
                  {isExpanded ? 'Show less' : `Show ${Object.keys(record.context).length - 3} more`}
                </button>
              )}
            </div>
          )}

          {record.tags && record.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3">
              {record.tags.map(tag => (
                <span key={tag} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {hasAttachments && (
            <div className="border-t border-gray-100 pt-3 mt-3">
              <div className="flex flex-wrap gap-2">
                {record.attachments.map((att, idx) => (
                  <a 
                    key={idx}
                    href={att.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs bg-indigo-50 text-indigo-600 px-2 py-1 rounded flex items-center gap-1 hover:bg-indigo-100"
                  >
                    <ExternalLink className="w-3 h-3" />
                    {att.name || `Document ${idx + 1}`}
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // List view - simplified for brevity
  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-all ${record.isPinned ? 'ring-2 ring-amber-400' : ''}`}>
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <RecordTypeBadge type={record.type} />
            {record.type === RecordType.TRANSACT && record.context?.transactionType && (
              <TransactionTypeBadge type={record.context.transactionType} />
            )}
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">{record.title}</h3>
          <p className="text-sm text-gray-600">{record.description}</p>
        </div>
        {record.type === RecordType.TRANSACT && record.context?.amount && (
          <div className={`text-right ${
            record.context.transactionType === TransactionType.INCOME ? 'text-green-700' : 'text-red-700'
          }`}>
            <div className="text-2xl font-bold">
              {record.context.transactionType === TransactionType.INCOME ? '+' : '-'}
              {formatAmount(record.context.amount, record.context.currency)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// --- RECORD EDITOR ---
const RecordEditor = ({ 
  record, 
  onSave, 
  onCancel, 
  resources = [],
  bookId 
}) => {
  const [formData, setFormData] = useState(record || {
    type: RecordType.GENERAL,
    title: '',
    description: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    context: {},
    measurements: [],
    measurementValues: {},
    tags: [],
    attachments: [],
    isPinned: false
  });

  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);

  const config = RECORD_CONFIGS[formData.type];

  const handleContextChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      context: { ...prev.context, [field]: value }
    }));
  };

  const handleMeasurementsChange = (measurements) => {
    setFormData(prev => ({ ...prev, measurements }));
  };

  const handleMeasurementValuesChange = (values) => {
    setFormData(prev => ({ ...prev, measurementValues: values }));
  };

  const handleFileUpload = async (file) => {
    setUploading(true);
    try {
      // Upload file logic here
      const url = URL.createObjectURL(file);
      setFormData(prev => ({
        ...prev,
        attachments: [...(prev.attachments || []), { name: file.name, url, type: file.type }]
      }));
    } catch (error) {
      console.error('Upload failed:', error);
    } finally {
      setUploading(false);
    }
  };

  const addTag = () => {
    if (!tagInput.trim()) return;
    if ((formData.tags || []).includes(tagInput.trim())) return;
    setFormData(prev => ({ ...prev, tags: [...(prev.tags || []), tagInput.trim()] }));
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(prev => ({ ...prev, tags: (prev.tags || []).filter(t => t !== tag) }));
  };

  const handleSubmit = () => {
    if (!formData.title) return;
    onSave?.({
      ...formData,
      id: formData.id || Math.random().toString(36).substr(2, 9),
      updatedAt: new Date().toISOString()
    });
  };

  return (
    <div className="flex-1 overflow-y-auto p-6">
      <div className="max-w-4xl mx-auto">
        <Card className="p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            {record ? 'Edit Record' : 'New Record'}
          </h2>

          <div className="space-y-6">
            {/* Record Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Record Type</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(RECORD_CONFIGS).map(([key, cfg]) => {
                  const Icon = cfg.icon;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        type: key, 
                        context: {}, 
                        measurements: key === RecordType.GENERAL ? [] : prev.measurements,
                        measurementValues: key === RecordType.GENERAL ? {} : prev.measurementValues 
                      }))}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center gap-2 ${
                        formData.type === key 
                          ? `${cfg.color} border-current shadow-sm` 
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="text-sm font-medium">{cfg.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Start Date</label>
                <input 
                  type="date"
                  value={formData.startDate}
                  onChange={e => setFormData(prev => ({ ...prev, startDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">End Date (Optional)</label>
                <input 
                  type="date"
                  value={formData.endDate || ''}
                  onChange={e => setFormData(prev => ({ ...prev, endDate: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
              <input 
                type="text"
                placeholder="Record title..."
                value={formData.title}
                onChange={e => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg text-lg font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea 
                rows={3}
                placeholder="Additional details..."
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            {/* GENERAL TYPE - Measurements Selector */}
            {formData.type === RecordType.GENERAL && (
              <>
                <MeasurementSelector 
                  selected={formData.measurements || []}
                  onChange={handleMeasurementsChange}
                />
                
                {formData.measurements && formData.measurements.length > 0 && (
                  <MeasurementFields 
                    measurements={formData.measurements}
                    values={formData.measurementValues || {}}
                    onChange={handleMeasurementValuesChange}
                  />
                )}
              </>
            )}

            {/* TRANSACT TYPE - Transaction-specific fields */}
            {formData.type === RecordType.TRANSACT && (
              <div className="bg-gray-50 p-5 rounded-lg border border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-3">Transaction Details</label>
                
                {/* Transaction Type */}
                <div className="mb-4">
                  <label className="block text-xs font-medium text-gray-600 mb-2">Type</label>
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleContextChange('transactionType', TransactionType.INCOME)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        formData.context.transactionType === TransactionType.INCOME
                          ? 'bg-green-50 text-green-700 border-green-500 shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <ArrowUpCircle className="w-5 h-5" />
                      <span className="font-medium">Income</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleContextChange('transactionType', TransactionType.EXPENSE)}
                      className={`p-3 rounded-lg border-2 transition-all flex items-center justify-center gap-2 ${
                        formData.context.transactionType === TransactionType.EXPENSE
                          ? 'bg-red-50 text-red-700 border-red-500 shadow-sm'
                          : 'border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <ArrowDownCircle className="w-5 h-5" />
                      <span className="font-medium">Expense</span>
                    </button>
                  </div>
                </div>

                {/* Amount and Currency */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Amount</label>
                    <input 
                      type="number"
                      step="0.01"
                      value={formData.context.amount || ''}
                      onChange={e => handleContextChange('amount', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Currency</label>
                    <select
                      value={formData.context.currency || 'USD'}
                      onChange={e => handleContextChange('currency', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-green-500"
                    >
                      {CURRENCIES.map(curr => (
                        <option key={curr.code} value={curr.code}>
                          {curr.symbol} {curr.code}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Other transaction fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
                    <input 
                      type="text"
                      value={formData.context.category || ''}
                      onChange={e => handleContextChange('category', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="e.g., Groceries, Salary, etc."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Merchant/Source</label>
                    <input 
                      type="text"
                      value={formData.context.merchant || ''}
                      onChange={e => handleContextChange('merchant', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Where/from whom"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Payment Method</label>
                    <input 
                      type="text"
                      value={formData.context.paymentMethod || ''}
                      onChange={e => handleContextChange('paymentMethod', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Cash, Card, Transfer, etc."
                    />
                  </div>
                </div>
              </div>
            )}

            {/* OTHER TYPE-SPECIFIC FIELDS */}
            {formData.type !== RecordType.GENERAL && formData.type !== RecordType.TRANSACT && config.fields.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  {config.label} Details
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {config.fields.map(field => (
                    <div key={field}>
                      <label className="block text-xs font-medium text-gray-600 mb-1 capitalize">
                        {field.replace(/([A-Z])/g, ' $1').trim()}
                      </label>
                      <input 
                        type="text"
                        value={formData.context?.[field] || ''}
                        onChange={e => handleContextChange(field, e.target.value)}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        placeholder={`Enter ${field.replace(/([A-Z])/g, ' $1').toLowerCase()}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Attachments</label>
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-indigo-400 transition-colors">
                <input 
                  type="file"
                  multiple
                  onChange={e => Array.from(e.target.files).forEach(handleFileUpload)}
                  className="hidden"
                  id="file-upload"
                  disabled={uploading}
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600">Click to upload files</p>
                  <p className="text-xs text-gray-400 mt-1">PDF, images, documents</p>
                </label>
              </div>
              
              {formData.attachments && formData.attachments.length > 0 && (
                <div className="mt-3 space-y-2">
                  {formData.attachments.map((att, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                      <span className="text-sm text-gray-700">{att.name}</span>
                      <button 
                        onClick={() => setFormData(prev => ({
                          ...prev,
                          attachments: prev.attachments.filter((_, i) => i !== idx)
                        }))}
                        className="text-red-600 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Tags</label>
              <div className="flex gap-2 mb-3">
                <input 
                  type="text"
                  placeholder="Add tag..."
                  value={tagInput}
                  onChange={e => setTagInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  className="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <Button variant="secondary" onClick={addTag}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              {formData.tags && formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {formData.tags.map(tag => (
                    <span key={tag} className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full text-sm flex items-center">
                      #{tag}
                      <button onClick={() => removeTag(tag)} className="ml-2 hover:text-indigo-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="pt-6 flex justify-end gap-3 border-t border-gray-100">
              <Button variant="secondary" onClick={onCancel}>
                <X className="w-4 h-4 mr-2" /> Cancel
              </Button>
              <Button onClick={handleSubmit} disabled={!formData.title}>
                <Check className="w-4 h-4 mr-2" /> {record ? 'Update' : 'Create'} Record
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// --- MAIN COMPONENT ---
export const DiaryRecordsView = ({ 
  bookId,
  customStyling = {},
  records = [],
  resources = [],
  onAddRecord,
  onUpdateRecord,
  onDeleteRecord,
  onPinRecord
}) => {
  const [viewMode, setViewMode] = useState('TIMELINE');
  const [editingRecord, setEditingRecord] = useState(null);
  const [filterType, setFilterType] = useState('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPinnedOnly, setShowPinnedOnly] = useState(false);
  const [displayMode, setDisplayMode] = useState('grid');

  const filteredRecords = useMemo(() => {
    return records
      .filter(r => {
        const matchesType = filterType === 'ALL' || r.type === filterType;
        const matchesSearch = 
          r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          r.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (r.tags || []).some(t => t.toLowerCase().includes(searchQuery.toLowerCase()));
        const matchesPinned = !showPinnedOnly || r.isPinned;
        return matchesType && matchesSearch && matchesPinned;
      })
      .sort((a, b) => {
        if (a.isPinned && !b.isPinned) return -1;
        if (!a.isPinned && b.isPinned) return 1;
        const da = new Date(a.startDate);
        const db = new Date(b.startDate);
        return db.getTime() - da.getTime();
      });
  }, [records, filterType, searchQuery, showPinnedOnly]);

  const handleSave = (record) => {
    if (editingRecord) {
      onUpdateRecord?.(record);
    } else {
      onAddRecord?.(record);
    }
    setEditingRecord(null);
    setViewMode('TIMELINE');
  };

  const handleEdit = (record) => {
    setEditingRecord(record);
    setViewMode('EDITOR');
  };

  const handleCancel = () => {
    setEditingRecord(null);
    setViewMode('TIMELINE');
  };

  const stats = useMemo(() => {
    const byType = {};
    Object.keys(RecordType).forEach(type => {
      byType[type] = records.filter(r => r.type === type).length;
    });
    return { total: records.length, ...byType };
  }, [records]);

  return (
    <div className="flex-1 overflow-y-auto flex-col h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {viewMode === 'TIMELINE' ? (
        <>
          {/* Header */}
          <div className="bg-white border-b border-gray-200 px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Records</h1>
                <p className="text-sm text-gray-500 mt-1">Comprehensive logging with complete context</p>
              </div>
              <Button onClick={() => { setEditingRecord(null); setViewMode('EDITOR'); }}
                style={{ backgroundColor: customStyling.primaryColor }}
                >
                <Plus className="w-4 h-4 mr-2" /> New Record
              </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input 
                    type="text"
                    placeholder="Search records..."
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDisplayMode(displayMode === 'grid' ? 'list' : 'grid')}
                  className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg border border-gray-200"
                >
                  {displayMode === 'grid' ? <List className="w-4 h-4" /> : <Grid3X3 className="w-4 h-4" />}
                </button>

                <button
                  onClick={() => setShowPinnedOnly(!showPinnedOnly)}
                  className={`px-3 py-2 rounded-lg border text-sm font-medium transition-all ${
                    showPinnedOnly 
                      ? 'bg-amber-50 text-amber-700 border-amber-200' 
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <Pin className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Type Filter */}
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                onClick={() => setFilterType('ALL')}
                style={{
                  backgroundColor: filterType === 'ALL' 
                    ? customStyling?.primaryColor 
                    : undefined
                }}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${
                  filterType === 'ALL'
                    ? 'bg-indigo-600 text-white shadow-sm'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                All ({stats.total})
              </button>
              {Object.entries(RECORD_CONFIGS).map(([key, cfg]) => {
                const Icon = cfg.icon;
                return (
                  <button
                    key={key}
                    onClick={() => setFilterType(key)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
                      filterType === key
                        ? `${cfg.color.replace('bg-', 'bg-opacity-100 bg-')} shadow-sm`
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {cfg.label} ({stats[key] || 0})
                  </button>
                );
              })}
            </div>
          </div>

          {/* Content */}
            {filteredRecords.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    {searchQuery || filterType !== 'ALL' ? 'No matching records' : 'No records yet'}
                  </h3>
                  <p className="text-gray-500 text-sm mb-6">
                    {searchQuery || filterType !== 'ALL'
                      ? 'Try adjusting your filters'
                      : 'Create comprehensive records with complete context'
                    }
                  </p>
                  {!searchQuery && filterType === 'ALL' && (
                    <Button onClick={() => { setEditingRecord(null); setViewMode('EDITOR'); }}>
                      <Plus className="w-4 h-4 mr-2" /> Create First Record
                    </Button>
                  )}
                </div>
              </div>
            ) : (
              <div className={displayMode === 'grid' ? "columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6" : "space-y-4"}>
                {filteredRecords.map(record => (
                  <RecordCard 
                    key={record.id}
                    record={record}
                    viewMode={displayMode}
                    resources={resources}
                    onEdit={handleEdit}
                    onDelete={onDeleteRecord}
                    onPin={onPinRecord}
                  />
                ))}
              </div>
            )}
        </>
      ) : (
        <RecordEditor 
          record={editingRecord}
          onSave={handleSave}
          onCancel={handleCancel}
          resources={resources}
          bookId={bookId}
        />
      )}
    </div>
  );
};

export default DiaryRecordsView;